// CenteringTool.jsx
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
// NOTE: HamburgerMenu lives in src/, while this file is in src/components/
import HamburgerMenu from "../HamburgerMenu";

/**
 * CenteringTool (stable drag, centered-initial-fit)
 * - Two boxes: OUTER (red) and INNER (orange)
 * - Mouse: drag edge squares (L/R = X only, T/B = Y only)
 * - Keyboard: arrows; Alt=1px, default=5px, Shift=10px; Tab toggles between boxes
 * - INNER stays inside OUTER; OUTER stays on screen
 * - NEW: Rotate mode — click "Rotate", drag the circular handle to rotate the image, click again to exit.
 */
export default function CenteringTool({ onUse, initialImageUrl = "" }) {
  const HEADER_OFFSET = 80; // keep tool UI completely below the global hamburger header

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(initialImageUrl);
  const [zoom, setZoom] = useState(1);

  // hamburger menu state
  const [menuOpen, setMenuOpen] = useState(false);

  // rotation
  const [rotateMode, setRotateMode] = useState(false);
  const [angleDeg, setAngleDeg] = useState(0);
  const rotateRef = useRef(null); // { startAngleDeg, baseAngleDeg, pointerId }

  // live canvas size
  const [canvas, setCanvas] = useState({ width: 0, height: 0 });

  // rects
  const [outer, setOuter] = useState({ x: 10, y: 10, w: 400, h: 560 });
  const [inner, setInner] = useState({ x: 40, y: 40, w: 340, h: 480 });

  // which box is selected for keyboard
  const [selected, setSelected] = useState("inner"); // 'outer' | 'inner'

  // drag state for box-resizing
  // { which:'outer'|'inner', edge:'l'|'r'|'t'|'b', start:{x,y}, base:{x,y,w,h}, pointerId }
  const dragRef = useRef(null);

  // run initial fit only once after canvas size is known
  const didInitialFit = useRef(false);

  // -------- paste / upload --------
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.type.includes("image")) {
          const file = it.getAsFile();
          if (file) setImgSrc(URL.createObjectURL(file));
          break;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setImgSrc(URL.createObjectURL(f));
  };

  // track svg size
  useLayoutEffect(() => {
    if (!svgRef.current) return;
    const el = svgRef.current;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setCanvas({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- clamps ----
  const pad = 10;

  const clampOuterRect = (r, size = canvas) => {
    const maxW = Math.max(20, size.width - pad * 2);
    const maxH = Math.max(20, size.height - pad * 2);
    let w = Math.min(Math.max(r.w, 20), maxW);
    let h = Math.min(Math.max(r.h, 20), maxH);
    let x = Math.min(Math.max(r.x, pad), size.width - pad - w);
    let y = Math.min(Math.max(r.y, pad), size.height - pad - h);
    return { x, y, w, h };
  };

  const clampInnerRect = (r, out) => {
    let x = Math.max(r.x, out.x);
    let y = Math.max(r.y, out.y);
    let w = Math.min(Math.max(r.w, 20), out.x + out.w - x);
    let h = Math.min(Math.max(r.h, 20), out.y + out.h - y);
    return { x, y, w, h };
  };

  const clampInnerX = (x, w, out) => Math.min(Math.max(x, out.x), out.x + out.w - w);
  const clampInnerY = (y, h, out) => Math.min(Math.max(y, out.y), out.y + out.h - h);

  // ---- centered initial fit (exactly once) ----
  const centerBoxes = (size = canvas) => {
    // Card aspect ratio: width / height ≈ 63 / 88
    const CARD_ASPECT = 63 / 88; // ≈ 0.7159
    const MAX_W_FRAC = 0.6;      // up to 60% of canvas width
    const MAX_H_FRAC = 0.6;      // up to 60% of canvas height

    // Try sizing by height first
    let targetH = size.height * MAX_H_FRAC;
    let targetW = targetH * CARD_ASPECT;

    // If that’s too wide, constrain by width instead
    if (targetW > size.width * MAX_W_FRAC) {
      targetW = size.width * MAX_W_FRAC;
      targetH = targetW / CARD_ASPECT;
    }

    // Center the OUTER rect
    const x = (size.width - targetW) / 2;
    const y = (size.height - targetH) / 2;

    const baseOuter = clampOuterRect(
      { x, y, w: targetW, h: targetH },
      size
    );
    setOuter(baseOuter);

    // Make INNER a tidy inset of OUTER (e.g., 10% margin on each side)
    const margin = 0.10;
    const inner0 = {
      x: baseOuter.x + baseOuter.w * margin,
      y: baseOuter.y + baseOuter.h * margin,
      w: baseOuter.w * (1 - 2 * margin),
      h: baseOuter.h * (1 - 2 * margin),
    };
    setInner(clampInnerRect(inner0, baseOuter));
  };

  // initial fit when we first get a non-zero canvas
  useEffect(() => {
    if (!didInitialFit.current && canvas.width && canvas.height) {
      didInitialFit.current = true;
      centerBoxes(canvas);
    }
  }, [canvas.width, canvas.height]);

  // on later canvas resizes, **only clamp**, do not refit (avoids jumping)
  useEffect(() => {
    if (!canvas.width || !canvas.height || !didInitialFit.current) return;
    setOuter((o) => clampOuterRect(o));
    setInner((i) => clampInnerRect(i, clampOuterRect(outer)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas.width, canvas.height]);

  // autofocus for keyboard
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // ---- keyboard (axis-locked) ----
  const handleKeyDown = (e) => {
    const NAV = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab"];
    if (!NAV.includes(e.key)) return;

    if (e.key === "Tab") {
      e.preventDefault();
      setSelected((s) => (s === "outer" ? "inner" : "outer"));
      return;
    }

    const step = e.shiftKey ? 10 : e.altKey ? 1 : 5;

    if (selected === "outer") {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setOuter((o) => {
          const next = clampOuterRect({ ...o, x: o.x - step });
          setInner((i) => clampInnerRect(i, next));
          return next;
        });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setOuter((o) => {
          const next = clampOuterRect({ ...o, x: o.x + step });
          setInner((i) => clampInnerRect(i, next));
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setOuter((o) => {
          const next = clampOuterRect({ ...o, y: o.y - step });
          setInner((i) => clampInnerRect(i, next));
          return next;
        });
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setOuter((o) => {
          const next = clampOuterRect({ ...o, y: o.y + step });
          setInner((i) => clampInnerRect(i, next));
          return next;
        });
      }
      return;
    }

    // inner selected
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setInner((i) => ({ ...i, x: clampInnerX(i.x - step, i.w, outer) }));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setInner((i) => ({ ...i, x: clampInnerX(i.x + step, i.w, outer) }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setInner((i) => ({ ...i, y: clampInnerY(i.y - step, i.h, outer) }));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setInner((i) => ({ ...i, y: clampInnerY(i.y + step, i.h, outer) }));
    }
  };

  // ---- pointer helpers ----
  const toLocal = (clientX, clientY) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  };
  const deg = (rad) => (rad * 180) / Math.PI;
  const rad = (degVal) => (degVal * Math.PI) / 180;

  const outerCenter = () => ({
    cx: outer.x + outer.w / 2,
    cy: outer.y + outer.h / 2,
  });

  // ---- rotation handlers ----
  const startRotateDrag = (e) => {
    if (!rotateMode) return;
    const { clientX, clientY, pointerId } = e.nativeEvent;
    const p = toLocal(clientX, clientY);
    const { cx, cy } = outerCenter();
    const startAngleDeg = deg(Math.atan2(p.y - cy, p.x - cx));
    rotateRef.current = { startAngleDeg, baseAngleDeg: angleDeg, pointerId };
    svgRef.current?.setPointerCapture?.(pointerId);
    e.preventDefault();
    e.stopPropagation();
  };

  const updateRotateDrag = (clientX, clientY) => {
    const rref = rotateRef.current;
    if (!rref) return;
    const p = toLocal(clientX, clientY);
    const { cx, cy } = outerCenter();
    const cur = deg(Math.atan2(p.y - cy, p.x - cx));
    const delta = cur - rref.startAngleDeg;
    setAngleDeg(normalizeAngle(rref.baseAngleDeg + delta));
  };

  const normalizeAngle = (a) => {
    let x = a;
    while (x <= -180) x += 360;
    while (x > 180) x -= 360;
    return x;
  };

  // which => edge => handler(e) for box resize
  const startEdgeDrag = (which) => (edge) => (e) => {
    if (rotateMode) return; // disable handle drags while rotating
    e.preventDefault();
    setSelected(which);
    const { clientX, clientY, pointerId } = e.nativeEvent;
    const p = toLocal(clientX, clientY);
    const base = which === "outer" ? outer : inner;
    dragRef.current = { which, edge, start: p, base: { ...base }, pointerId };
    svgRef.current?.setPointerCapture?.(pointerId); // capture on SVG
  };

  const onPointerMove = (e) => {
    const { clientX, clientY } = e.nativeEvent;

    // Rotation first if active
    if (rotateRef.current) {
      updateRotateDrag(clientX, clientY);
      return;
    }

    const d = dragRef.current;
    if (!d) return;

    const p = toLocal(clientX, clientY);
    const dx = p.x - d.start.x;
    const dy = p.y - d.start.y;

    if (d.which === "outer") {
      let next = { ...d.base };
      if (d.edge === "l") {
        const nx = d.base.x + dx;
        const w = d.base.w + (d.base.x - nx);
        next = clampOuterRect({ ...next, x: nx, w });
      } else if (d.edge === "r") {
        const w = d.base.w + dx;
        next = clampOuterRect({ ...next, w });
      } else if (d.edge === "t") {
        const ny = d.base.y + dy;
        const h = d.base.h + (d.base.y - ny);
        next = clampOuterRect({ ...next, y: ny, h });
      } else if (d.edge === "b") {
        const h = d.base.h + dy;
        next = clampOuterRect({ ...next, h });
      }
      setOuter(next);
      setInner((i) => clampInnerRect(i, next));
    } else {
      // inner
      let next = { ...d.base };
      if (d.edge === "l") {
        const nx = d.base.x + dx;
        const w = d.base.w + (d.base.x - nx);
        next = clampInnerRect({ ...next, x: nx, w }, outer);
      } else if (d.edge === "r") {
        const w = d.base.w + dx;
        next = clampInnerRect({ ...next, w }, outer);
      } else if (d.edge === "t") {
        const ny = d.base.y + dy;
        const h = d.base.h + (d.base.y - ny);
        next = clampInnerRect({ ...next, y: ny, h }, outer);
      } else if (d.edge === "b") {
        const h = d.base.h + dy;
        next = clampInnerRect({ ...next, h }, outer);
      }
      setInner(next);
    }
  };

  const endPointerOps = () => {
    // Release captures safely
    const r = rotateRef.current;
    if (r) {
      try { svgRef.current?.releasePointerCapture?.(r.pointerId); } catch {}
      rotateRef.current = null;
    }
    const d = dragRef.current;
    if (d) {
      try { svgRef.current?.releasePointerCapture?.(d.pointerId); } catch {}
      dragRef.current = null;
    }
  };

  // ---- metrics ----
  const compute = () => {
    const left = inner.x - outer.x;
    const right = outer.x + outer.w - (inner.x + inner.w);
    const top = inner.y - outer.y;
    const bottom = outer.y + outer.h - (inner.y + inner.h);
    const lr = Math.max(0, left) + Math.max(0, right);
    const tb = Math.max(0, top) + Math.max(0, bottom);
    const lPct = lr ? (left / lr) * 100 : 50;
    const rPct = lr ? (right / lr) * 100 : 50;
    const tPct = tb ? (top / tb) * 100 : 50;
    const bPct = tb ? (bottom / tb) * 100 : 50;
    return {
      lPct: +lPct.toFixed(1),
      rPct: +rPct.toFixed(1),
      tPct: +tPct.toFixed(1),
      bPct: +bPct.toFixed(1),
    };
  };

  const { lPct, rPct, tPct, bPct } = compute();

  // ---- clipboard helpers ----
  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
  };

  // rotation handle display position (based on OUTER rect)
  const rotationVisual = (() => {
    const { cx, cy } = outerCenter();
    const radius = Math.max(60, Math.min(outer.w, outer.h) * 0.35);
    const a = rad(angleDeg);
    const hx = cx + radius * Math.cos(a);
    const hy = cy + radius * Math.sin(a);
    return { cx, cy, hx, hy, radius };
  })();

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "#0b0b0b", color: "#fff" }}>
      {/* Global site header (fixed). Contains logo + hamburger + dropdown. */}
      <HamburgerMenu menuOpen={menuOpen} toggleMenu={setMenuOpen} />

      {/* Spacer to ensure our tool UI starts below the fixed hamburger header */}
      <div style={{ height: HEADER_OFFSET }} />

      {/* Tool toolbar (kept above the canvas content and below hamburger via top offset) */}
      <div
        style={{
          padding: 12,
          display: "flex",
          gap: 10,
          alignItems: "center",
          borderBottom: "1px solid #222",
          userSelect: "none",
          position: "sticky",
          top: HEADER_OFFSET,
          zIndex: 10,
          background: "#0b0b0b",
        }}
      >
        <strong>Centering Tool</strong>
        <input type="file" accept="image/*" onChange={onFile} />
        <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))} style={btn}>−</button>
        <span style={{ width: 60, textAlign: "center" }}>{zoom.toFixed(2)}x</span>
        <button onClick={() => setZoom((z) => Math.min(4, z + 0.1))} style={btn}>+</button>

        <button style={{ ...btn, background: "#374151" }} onClick={() => centerBoxes()}>
          Center Boxes
        </button>

        {/* Rotate toggle + reset */}
        <div style={{ display: "flex", gap: 8, marginLeft: 8 }}>
          <button
            style={{ ...btn, background: rotateMode ? "#8b5cf6" : "#4b5563" }}
            onClick={() => setRotateMode((on) => !on)}
            title="Toggle rotate mode (drag the circular handle)"
          >
            {rotateMode ? "Finish Rotate" : "Rotate"}
          </button>
          <button
            style={{ ...btn, background: "#6b7280" }}
            onClick={() => setAngleDeg(0)}
            title="Reset rotation to 0°"
          >
            Reset Rotation
          </button>
          <div style={{ alignSelf: "center", opacity: 0.8 }}>Angle: {angleDeg.toFixed(1)}°</div>
        </div>

        <div style={{ display: "flex", gap: 6, marginLeft: "auto", alignItems: "center" }}>
          <span style={{ opacity: 0.8 }}>Selected:</span>
          <button
            style={{ ...btn, background: selected === "outer" ? "#ef4444" : "#374151" }}
            onClick={() => setSelected("outer")}
            title="Select Outer (Tab toggles)"
          >
            Outer
          </button>
          <button
            style={{ ...btn, background: selected === "inner" ? "#f59e0b" : "#374151" }}
            onClick={() => setSelected("inner")}
            title="Select Inner (Tab toggles)"
          >
            Inner
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Badge label="L | R" value={`${lPct}% | ${rPct}%`} />
          <Badge label="T | B" value={`${tPct}% | ${bPct}%`} />

          {/* Separate copy buttons (with labels in the copied text) */}
          <button
            style={{ ...btn, background: "#10b981" }}
            onClick={() => copyToClipboard(`L|R ${lPct}% | ${rPct}%`)}
            title="Copy Left|Right to clipboard"
          >
            Copy L|R
          </button>
          <button
            style={{ ...btn, background: "#10b981" }}
            onClick={() => copyToClipboard(`T|B ${tPct}% | ${bPct}%`)}
            title="Copy Top|Bottom to clipboard"
          >
            Copy T|B
          </button>

          {/* All numbers: copy labeled pairs and notify parent if provided */}
          <button
            style={{ ...btn, background: "#22c55e" }}
            onClick={() => {
              copyToClipboard(`L|R ${lPct}% | ${rPct}%, T|B ${tPct}% | ${bPct}%`);
              onUse?.({ lPct, rPct, tPct, bPct, angleDeg });
            }}
            title="Copy both labeled pairs"
          >
            Copy numbers
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          flex: 1,
          background: "#000",
          userSelect: "none",
          outline: "none",
          overflow: "hidden", // prevents zoomed image from spilling over the tool area
        }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={() => containerRef.current?.focus()}
        title={rotateMode ? "Rotate mode: drag the purple handle" : "Click canvas, then use arrows. Tab toggles inner/outer."}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt="card"
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              maxWidth: "100%",
              maxHeight: "100%",
              // IMPORTANT: rotate first, then scale keeps aspect within bounds nicely
              transform: `rotate(${angleDeg}deg) scale(${zoom})`,
              transformOrigin: "center center",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        ) : (
          <div style={{ color: "#888", display: "grid", placeItems: "center", height: "100%" }}>
            Paste or upload a card image to begin.
          </div>
        )}

        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ position: "absolute", inset: 0, touchAction: "none" }}
          onPointerMove={onPointerMove}
          onPointerUp={endPointerOps}
          onPointerCancel={endPointerOps}
        >
          {/* Rotation UI (visible only in rotate mode) */}
          {rotateMode && (
            <g>
              {/* guide circle */}
              <circle
                cx={rotationVisual.cx}
                cy={rotationVisual.cy}
                r={rotationVisual.radius}
                fill="none"
                stroke="#8b5cf6"
                strokeDasharray="6 6"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
              {/* guide line from center to handle */}
              <line
                x1={rotationVisual.cx}
                y1={rotationVisual.cy}
                x2={rotationVisual.hx}
                y2={rotationVisual.hy}
                stroke="#8b5cf6"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
              {/* drag handle */}
              <circle
                cx={rotationVisual.hx}
                cy={rotationVisual.hy}
                r={12}
                fill="#8b5cf6"
                stroke="#000"
                strokeWidth="1"
                style={{ cursor: "grab" }}
                onPointerDown={startRotateDrag}
              />
            </g>
          )}

          {/* Resizable rectangles (disabled while rotating) */}
          <RectWithAxisHandles
            rect={outer}
            color="#ff3b30"
            selected={selected === "outer"}
            onSelect={() => !rotateMode && setSelected("outer")}
            onEdgePointerDown={startEdgeDrag("outer")}
            disabled={rotateMode}
          />
          <RectWithAxisHandles
            rect={inner}
            color="#ffaa33"
            selected={selected === "inner"}
            onSelect={() => !rotateMode && setSelected("inner")}
            onEdgePointerDown={startEdgeDrag("inner")}
            disabled={rotateMode}
          />
        </svg>
      </div>
    </div>
  );
}

function RectWithAxisHandles({ rect, color, selected, onSelect, onEdgePointerDown, disabled }) {
  const { x, y, w, h } = rect;
  const s = 12;

  const handle = (cx, cy, edge, key, cursor) => (
    <rect
      key={key}
      x={cx - s / 2}
      y={cy - s / 2}
      width={s}
      height={s}
      fill={color}
      stroke="#000"
      strokeWidth="1"
      style={{ cursor: disabled ? "not-allowed" : cursor, opacity: disabled ? 0.4 : 1 }}
      onPointerDown={disabled ? undefined : onEdgePointerDown(edge)}
    />
  );

  return (
    <g onClick={onSelect} style={{ cursor: disabled ? "default" : "default" }}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="rgba(0,0,0,0)"
        stroke={color}
        strokeWidth={selected ? 4 : 2.5}
        strokeDasharray={selected ? "6 4" : "0"}
        vectorEffect="non-scaling-stroke"
      />
      {/* edge handles only */}
      {handle(x + w / 2, y, "t", "t", "ns-resize")}
      {handle(x + w / 2, y + h, "b", "b", "ns-resize")}
      {handle(x, y + h / 2, "l", "l", "ew-resize")}
      {handle(x + w, y + h / 2, "r", "r", "ew-resize")}
    </g>
  );
}

function Badge({ label, value }) {
  return (
    <div
      style={{
        background: "#1f2937",
        padding: "6px 10px",
        borderRadius: 8,
        display: "inline-flex",
        gap: 8,
        alignItems: "center",
      }}
    >
      <strong>{label}</strong>
      <span style={{ color: "#a7f3d0" }}>{value}</span>
    </div>
  );
}

const btn = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "6px 10px",
  cursor: "pointer",
};
